import React, { FormEvent, FunctionComponent, ReactElement, useState } from 'react'
import InputMask from 'react-input-mask'
import './FormField.scss'
import { useScrollToInputOnVirtualKeyboardOpened } from '@hooks/useScrollToInputOnVirtualKeyboardOpened'

interface Props {
  type?: 'text' | 'password' | 'email' | 'number'
  placeholder?: string
  readonly?: boolean
  disabled?: boolean
  name?: string
  id?: string
  label?: string
  value?: string
  onChange?: (e: string) => void
  error?: string
  className?: string
  mask?: Record<string, string>
  min?: string
  max?: string
}

export const FormField: FunctionComponent<Props> = ({
  type = 'text',
  className = '',
  readonly = false,
  disabled = false,
  placeholder = undefined,
  label = undefined,
  onChange = undefined,
  name = undefined,
  id = undefined,
  error = undefined,
  value = undefined,
  min = undefined,
  max = undefined,
  mask = undefined,
}: Props): ReactElement => {
  const [hasFocus, setHasFocus] = useState(false)
  const handleChange = ({ currentTarget }: FormEvent<HTMLInputElement>): void => {
    if (max && parseInt(currentTarget.value) > parseInt(max)) currentTarget.value = max
    if (min && parseInt(currentTarget.value) < parseInt(min)) currentTarget.value = min
    if (mask) currentTarget.value = currentTarget.value.toUpperCase()

    if (onChange) onChange(currentTarget.value)
  }

  const focusValue = hasFocus || !!value
  const message = error || label
  const labelComp: ReactElement | undefined = label ? <span className="FormField-label">{message}</span> : undefined
  const finalClassName = `FormField ${className}${focusValue ? ' focusValue' : ''}${error ? ' hasErrors' : ''}${readonly ? ' readOnly' : ''
    }`

  const { scrollToRef } = useScrollToInputOnVirtualKeyboardOpened()

  return (
    <label ref={scrollToRef as any} htmlFor={id} className={finalClassName}>
      {labelComp}
      {mask ? (
        <InputMask
          mask={Object.keys(mask).join('')}
          formatChars={mask}
          className="FormField-input masked"
          type={type}
          name={name}
          id={id}
          readOnly={readonly}
          placeholder={placeholder}
          defaultValue={value}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          onChange={handleChange}
          disabled={disabled}
        />
      ) : (
        <input
          className="FormField-input"
          type={type}
          name={name}
          id={id}
          key={id}
          readOnly={readonly}
          placeholder={placeholder}
          defaultValue={value}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          onChange={handleChange}
          disabled={disabled}
        />
      )}
    </label>
  )
}

export default FormField
